interface ErrorEntry {
  count: number;
  lastOccurrence: Date;
  firstOccurrence: Date;
}

interface ErrorStats {
  componentErrors: Map<string, ErrorEntry>;
  globalErrorCount: number;
  isProduction: boolean;
}

class ErrorLogger {
  private stats: ErrorStats;
  private readonly MAX_ERRORS_PER_COMPONENT = 10;
  private readonly ERROR_RESET_INTERVAL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.stats = {
      componentErrors: new Map(),
      globalErrorCount: 0,
      isProduction: process.env.NODE_ENV === 'production'
    };

    // Reset error counts periodically
    setInterval(() => {
      this.resetOldErrors();
    }, this.ERROR_RESET_INTERVAL);
  }

  logError(error: Error, component?: string, additionalInfo?: Record<string, unknown>): boolean {
    const componentName = component || 'Unknown';
    const errorKey = `${componentName}:${error.message}`;
    
    // Update component error stats
    const existing = this.stats.componentErrors.get(errorKey);
    if (existing) {
      existing.count++;
      existing.lastOccurrence = new Date();
    } else {
      this.stats.componentErrors.set(errorKey, {
        count: 1,
        firstOccurrence: new Date(),
        lastOccurrence: new Date()
      });
    }

    this.stats.globalErrorCount++;

    // Check if component has exceeded error threshold (circuit breaker)
    const entry = this.stats.componentErrors.get(errorKey)!;
    if (entry.count > this.MAX_ERRORS_PER_COMPONENT) {
      if (!this.stats.isProduction) {
        console.warn(`🔴 Circuit breaker: ${componentName} has exceeded error threshold`, error);
      }
      return false; // Don't continue processing
    }

    // Log error (quiet in production)
    if (!this.stats.isProduction) {
      console.group(`🔴 Error in ${componentName}`);
      console.error('Error:', error);
      console.error('Stack:', error.stack);
      if (additionalInfo) {
        console.error('Additional info:', additionalInfo);
      }
      console.error('Error count for this component:', entry.count);
      console.groupEnd();
    } else {
      // In production, just log minimal info
      console.error(`Error in ${componentName}:`, error.message);
    }

    return true; // Continue processing
  }

  isComponentHealthy(component: string): boolean {
    const errorKeys = Array.from(this.stats.componentErrors.keys());
    for (const errorKey of errorKeys) {
      if (errorKey.startsWith(`${component}:`)) {
        const entry = this.stats.componentErrors.get(errorKey);
        if (entry && entry.count > this.MAX_ERRORS_PER_COMPONENT) {
          return false;
        }
      }
    }
    return true;
  }

  getComponentErrorCount(component: string): number {
    let total = 0;
    const errorKeys = Array.from(this.stats.componentErrors.keys());
    for (const errorKey of errorKeys) {
      if (errorKey.startsWith(`${component}:`)) {
        const entry = this.stats.componentErrors.get(errorKey);
        if (entry) {
          total += entry.count;
        }
      }
    }
    return total;
  }

  resetComponentErrors(component: string): void {
    const keysToDelete = Array.from(this.stats.componentErrors.keys())
      .filter(key => key.startsWith(`${component}:`));
    
    keysToDelete.forEach(key => {
      this.stats.componentErrors.delete(key);
    });
  }

  private resetOldErrors(): void {
    const fiveMinutesAgo = new Date(Date.now() - this.ERROR_RESET_INTERVAL);
    const errorKeys = Array.from(this.stats.componentErrors.keys());
    
    for (const errorKey of errorKeys) {
      const entry = this.stats.componentErrors.get(errorKey);
      if (entry && entry.lastOccurrence < fiveMinutesAgo) {
        this.stats.componentErrors.delete(errorKey);
      }
    }
  }

  getStats(): Readonly<ErrorStats> {
    return {
      ...this.stats,
      componentErrors: new Map(this.stats.componentErrors)
    };
  }
}

// Singleton instance
export const errorLogger = new ErrorLogger();